import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { z } from 'zod'
import { useAuth } from '../lib/auth.js'
import { Button } from '../components/Button.js'
import { Field, Input } from '../components/Field.js'
import { Icon } from '../components/Icon.js'

const resetPasswordSearchSchema = z.object({
  // Better Auth bounces the browser here with ?token=… after it validates the
  // emailed reset link. Optional so we can render an "invalid link" state when
  // someone lands here without one.
  token: z.string().optional(),
})

export const Route = createFileRoute('/_auth/reset-password')({
  validateSearch: resetPasswordSearchSchema,
  component: ResetPasswordPage,
})

function ResetPasswordPage() {
  const navigate = useNavigate()
  const { token } = useSearch({ from: '/_auth/reset-password' })
  const { resetPassword } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    if (!token) {
      setError('This reset link is invalid or has expired.')
      return
    }
    setSubmitting(true)
    try {
      await resetPassword(token, password)
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password')
    } finally {
      setSubmitting(false)
    }
  }

  if (!token) {
    return (
      <div className="space-y-6">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold text-fg">Reset link invalid</h1>
          <p className="text-sm text-fg-muted">
            This reset link is invalid or has expired.
          </p>
        </header>
        <div className="flex items-start gap-2 rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">
          <Icon name="alert" size={14} className="mt-0.5 text-fg-subtle" />
          <p>Request a new link and we will email you a fresh one.</p>
        </div>
        <p className="text-center text-sm text-fg-muted">
          <Link
            to="/forgot-password"
            className="font-medium text-[var(--color-primary)] hover:underline"
          >
            Request a new link
          </Link>
        </p>
      </div>
    )
  }

  if (done) {
    return (
      <div className="space-y-6">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold text-fg">Password updated</h1>
          <p className="text-sm text-fg-muted">
            Your password has been changed. You can now sign in with it.
          </p>
        </header>
        <Button onClick={() => navigate({ to: '/login' })} className="w-full">
          Sign in
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-fg">Set a new password</h1>
        <p className="text-sm text-fg-muted">Choose a new password for your account.</p>
      </header>

      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="New password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            placeholder="••••••••"
          />
        </Field>
        <Field label="Confirm new password">
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            placeholder="••••••••"
          />
        </Field>

        {error ? (
          <div className="rounded-md border border-danger-border bg-danger-tint px-3 py-2 text-sm text-danger">
            {error}
          </div>
        ) : null}

        <Button type="submit" loading={submitting} className="w-full">
          Update password
        </Button>
      </form>

      <p className="text-center text-sm text-fg-muted">
        <Link
          to="/login"
          className="font-medium text-[var(--color-primary)] hover:underline"
        >
          Back to sign in
        </Link>
      </p>
    </div>
  )
}
